import { memo, useEffect, useMemo, useRef } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { formatMinutesToTime } from '@/helpers';

import { measureTextWidth } from '../../../../common/utils/measure-text';
import { STICKY_LABEL_HORIZONTAL_PADDING_PX } from '../../../constants/assignment-text';
import { PLANNER_STICKY_LEFT_PX, REM_TO_PX } from '../../../constants/sizes';
import { useTextMeasurement } from '../../../contexts/text-measurement-context';
import { buildTimeOffSegments, TimeOffSegment } from '../../../helpers/time-off-day-stacking';
import usePlannerStore from '../../../store';
import { TimeOffBlockType } from '../../../types';

interface StickyTimeOffLabelsProps {
  timeOffs: TimeOffBlockType[];
  cellWidth: number;
  cellHeight: number;
  /** Per-date capacity resolver (minutes/day), used for segment layout. */
  getCapacityMinutes: (dateKey: string) => number;
  dates: dayjs.Dayjs[];
  onStickySegmentsChange: (segments: Map<number, Set<string>>) => void;
}

/** A multi-day segment whose sticky label may render, with measured duration. */
interface StickyTimeOffFragment {
  segment: TimeOffSegment;
  /** Bar width in rem. */
  duration: number;
  /** Label text. */
  title: string;
}

/**
 * Determines which time-off segments have a label that fits within their bar.
 * Only multi-day segments are eligible — single-day segments show an icon.
 */
const filterVisibleFragments = (
  fragments: StickyTimeOffFragment[],
  textReferenceElement: HTMLElement
): {
  visibleFragments: StickyTimeOffFragment[];
  stickySegmentsByTimeOff: Map<number, Set<string>>;
} => {
  const stickySegmentsByTimeOff = new Map<number, Set<string>>();
  const visible: StickyTimeOffFragment[] = [];

  for (const fragment of fragments) {
    const barWidthPx = fragment.duration * REM_TO_PX;
    const textWidth = measureTextWidth(fragment.title, textReferenceElement);
    const availableWidth = barWidthPx - STICKY_LABEL_HORIZONTAL_PADDING_PX;
    const fits = textWidth <= availableWidth;

    if (fits) {
      visible.push(fragment);
      const timeOffId = fragment.segment.timeOff.id;
      const dates = stickySegmentsByTimeOff.get(timeOffId) ?? new Set<string>();
      dates.add(fragment.segment.startDate.startOf('day').toISOString());
      stickySegmentsByTimeOff.set(timeOffId, dates);
    }
  }

  return { visibleFragments: visible, stickySegmentsByTimeOff };
};

/**
 * Renders sticky text labels for multi-day time-off bars. Uses the same
 * structural pattern as sticky assignment labels and month headers: a
 * flow-based container constrains the sticky span to the bar's horizontal
 * range, while position:sticky references the outer #planner-container
 * scrollport so the label stays pinned while scrolling left/right.
 *
 * pointer-events-none ensures clicks pass through to the real time-off blocks.
 * When a label overflows the bar width, it is not rendered here and the
 * ScrollableText inside the block is shown instead.
 */
export const StickyTimeOffLabels = memo(
  ({
    timeOffs,
    cellWidth,
    cellHeight,
    getCapacityMinutes,
    dates,
    onStickySegmentsChange,
  }: StickyTimeOffLabelsProps) => {
    const { t } = useTranslation('authenticated.planner');
    const { assignmentTextRef } = useTextMeasurement();

    const { hoveredTimeOffId } = usePlannerStore(
      useShallow(state => ({ hoveredTimeOffId: state.hoveredTimeOffId }))
    );

    const baseDate = dates[0];

    // Build fragments from multi-day segments. Single-day segments are skipped
    // (they show the in-bar icon, not a label).
    const fragments = useMemo<StickyTimeOffFragment[]>(() => {
      if (!baseDate) return [];
      const segments = buildTimeOffSegments(timeOffs, getCapacityMinutes, cellHeight);
      return segments
        .filter(segment => {
          const days = segment.endDate.startOf('day').diff(segment.startDate.startOf('day'), 'day');
          return days >= 1;
        })
        .map(segment => {
          const days = segment.endDate.startOf('day').diff(segment.startDate.startOf('day'), 'day');
          const duration = (days + 1) * cellWidth;
          const title = t('timeOff.blockLabel', {
            title: segment.timeOff.title,
            time: formatMinutesToTime(segment.dayMinutes),
          });
          return { segment, duration, title };
        });
    }, [timeOffs, getCapacityMinutes, cellHeight, cellWidth, baseDate, t]);

    const { visibleFragments, stickySegmentsByTimeOff } = useMemo(() => {
      if (!assignmentTextRef) {
        return {
          visibleFragments: [],
          stickySegmentsByTimeOff: new Map<number, Set<string>>(),
        };
      }
      return filterVisibleFragments(fragments, assignmentTextRef);
    }, [fragments, assignmentTextRef]);

    // Notify parent which segments have active sticky labels.
    const prevSegmentsRef = useRef<Map<number, Set<string>>>(new Map());
    useEffect(() => {
      const prev = prevSegmentsRef.current;
      let changed = prev.size !== stickySegmentsByTimeOff.size;
      if (!changed) {
        for (const [id, segDates] of stickySegmentsByTimeOff.entries()) {
          const prevDates = prev.get(id);
          if (
            !prevDates ||
            prevDates.size !== segDates.size ||
            ![...segDates].every(d => prevDates.has(d))
          ) {
            changed = true;
            break;
          }
        }
      }
      if (changed) {
        prevSegmentsRef.current = stickySegmentsByTimeOff;
        onStickySegmentsChange(stickySegmentsByTimeOff);
      }
    }, [stickySegmentsByTimeOff, onStickySegmentsChange]);

    if (!baseDate) return null;

    return (
      // Rendered INSIDE the overlay between the time-off blocks and the assignment
      // bars. z-0 (no lift) so DOM order decides: it paints ABOVE the blocks
      // (rendered before it) but BELOW the assignment bars and their sticky labels
      // (rendered after / at higher z), so assignment text always wins on overlap.
      <div className="pointer-events-none absolute inset-0 z-0">
        {visibleFragments.map(({ segment, duration, title }) => {
          const startOffset =
            segment.startDate.startOf('day').diff(baseDate.startOf('day'), 'day') * cellWidth;
          const isHovered = hoveredTimeOffId === segment.timeOff.id;

          return (
            <div
              key={segment.key}
              className="absolute left-0 right-0 flex items-center transition-transform duration-150"
              style={{
                top: `${segment.rect.topPx}px`,
                height: `${segment.rect.heightPx}px`,
                transform: isHovered ? 'translateY(-0.25rem)' : undefined,
              }}
            >
              {/* Inner flow div constrains the sticky range to the bar's span. */}
              <div
                className="flex h-full items-center overflow-clip"
                style={{ marginLeft: `${startOffset}rem`, width: `${duration}rem` }}
              >
                <span
                  className="sticky truncate whitespace-nowrap px-1.5 text-[10px] font-medium text-time-off-foreground-strong"
                  style={{ left: `${PLANNER_STICKY_LEFT_PX}px`, maxWidth: `${duration}rem` }}
                >
                  {title}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
);

StickyTimeOffLabels.displayName = 'StickyTimeOffLabels';
